import { APP_URLS } from 'constants/urls';
import { TFunction } from 'i18next';
import { BreadCrumb } from '@lib/components/BreadCrumbs/types';
import { Values } from '@lib/interfaces/form';
import { NEW } from '@lib/enums/common';
import { DEFAULT_LIST_PARAMS } from '@lib/enums/urls';

type Options = {
  pathname: string;
  t: TFunction<'translation', undefined>;
  entity?: Values;
};

export function getBreadCrumbsDataByKey({ pathname, t, entity }: Options) {
  const dashboardHomeItem = {
    label: t('home-page'),
    path: APP_URLS.dashboard.home.getPathWithQuery(),
  };
  const sitesItem = {
    label: t('sites'),
    path: APP_URLS.dashboard.sites.index.getPathWithQuery(),
  };
  const viewSiteItem = {
    label: t('view-site'),
    path: APP_URLS.dashboard.sites.view.getDynamicPath({
      pathParts: {
        id: `${entity?.id}`,
      },
    }),
  };
  const sectorsItem = {
    label: t('sectors'),
    path: APP_URLS.dashboard.sectors.index.getPathWithQuery(),
  };
  const viewSectorItem = {
    label: t('view-sector'),
    path: APP_URLS.dashboard.sectors.view.getDynamicPath({
      pathParts: {
        id: `${entity?.id}`,
      },
    }),
  };
  const formTemplatesItem = {
    label: t('form-templates'),
    path: APP_URLS.dashboard.formTemplates.index.getPathWithQuery(),
  };
  const assetsItem = {
    label: t('assets'),
    path: APP_URLS.dashboard.assets.index.getPathWithQuery(),
  };
  const tasksItem = {
    label: t('tasks'),
    path: APP_URLS.dashboard.tasks.index.getPathWithQuery(),
  };
  const interventionsItem = {
    label: t('interventions'),
    path: APP_URLS.dashboard.interventions.index.getPathWithQuery(),
  };
  const teamsItem = {
    label: t('teams'),
    path: APP_URLS.dashboard.teams.index.getPathWithQuery(),
  };
  const viewTeamItem = {
    label: t('team-details'),
    path: APP_URLS.dashboard.teams.view.getDynamicPath({
      pathParts: {
        id: `${entity?.id}`,
      },
    }),
  };
  const usersItem = {
    label: t('members'),
    path: APP_URLS.dashboard.users.index.getPathWithQuery(),
  };
  const checklistsItem = {
    label: t('forms-checklists-templates'),
    path: APP_URLS.dashboard.formChecklists.index.getPathWithQuery(),
  };
  const ssoItem = {
    label: t('single-sign-on-sso'),
    path: APP_URLS.dashboard.sso.index.getPathWithQuery(),
  };

  const breadcrumbMap = [
    {
      condition: APP_URLS.dashboard.home.isTheSameUrlAs(pathname),
      data: [{ label: t('home-page') }],
    },
    {
      condition: APP_URLS.dashboard.homeWidgets.isTheSameUrlAs(pathname),
      data: [dashboardHomeItem, { label: t('home.manage-widget-templates') }],
    },
    {
      condition: APP_URLS.dashboard.homeEdit.isTheSameUrlAs(pathname),
      data: [dashboardHomeItem, { label: t('home.manage-widget-templates') }],
    },
    {
      condition: APP_URLS.dashboard.homeWidgetsCreate.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('home.manage-widget-templates'),
          path: APP_URLS.dashboard.homeWidgets.getPathWithQuery(),
        },
        { label: t('home.create-widget-template') },
      ],
    },
    {
      condition: APP_URLS.dashboard.overview.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('overview'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.timeline.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('timeline'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.monitoring.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('monitoring'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.profile.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('profile'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.publicApi.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('public-api'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.users.index.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('members'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.users.view.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        usersItem,
        {
          label: t('view-personal-info'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.users.viewAccess.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        usersItem,
        {
          label: t('view-access-rights'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.users.editAccess.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        usersItem,
        {
          label: t('view-membership'),
          path: APP_URLS.dashboard.users.view.getDynamicPath({
            pathParts: { id: `${entity?.id}` },
          }),
        },
        {
          label: t('edit-access-rights'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.teams.index.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('teams'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.teams.add.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        teamsItem,
        {
          label: t('create-team'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.teams.edit.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        teamsItem,
        {
          label: t('edit-team'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.teams.view.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        teamsItem,
        {
          label: t('view-team'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.teams.members.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        teamsItem,
        viewTeamItem,
        {
          label: t('team-members'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.sites.index.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('sites'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.sites.add.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('sites'),
          path: APP_URLS.dashboard.sites.index.getPathWithQuery(),
        },
        {
          label: t('create-new-site'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.sites.edit.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        sitesItem,
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.sites.view.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        sitesItem,
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.sites.areas.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        sitesItem,
        viewSiteItem,
        {
          label: t('site-areas'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.sites.users.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        sitesItem,
        viewSiteItem,
        {
          label: t('team'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.sites.viewCodeOfConduct.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.id}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        sitesItem,
        viewSiteItem,
        {
          label: t('view-code-of-conduct'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.sites.editCodeOfConduct.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.id}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        sitesItem,
        viewSiteItem,
        {
          label: t('edit-code-of-conduct'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.sectors.index.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [dashboardHomeItem, { label: t('sectors') }],
    },
    {
      condition: APP_URLS.dashboard.sectors.add.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        sectorsItem,
        {
          label: t('create-new-sector'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.sectors.edit.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        sectorsItem,
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.sectors.view.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        sectorsItem,
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.sectors.sites.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        sectorsItem,
        viewSectorItem,
        {
          label: t('linked-sites'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.sectors.users.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        sectorsItem,
        viewSectorItem,
        {
          label: t('members'),
        },
      ],
    },
    {
      condition:
        APP_URLS.dashboard.formTemplates.index.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('form-templates'),
        },
      ],
    },
    {
      condition:
        APP_URLS.dashboard.formTemplates.archived.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('form-templates'),
        },
      ],
    },
    {
      condition:
        APP_URLS.dashboard.formTemplates.categories.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        formTemplatesItem,
        {
          label: t('categories'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.formTemplates.add.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        formTemplatesItem,
        {
          label: t('create-new-form'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.formTemplates.editVersion.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.id}`,
            versionId: `${entity?.versionId}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        formTemplatesItem,
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.formTemplates.viewVersion.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.id}`,
            versionId: `${entity?.versionId}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        formTemplatesItem,
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition:
        APP_URLS.dashboard.formTemplates.viewLinkedTasks.isTheSameUrlAs(
          pathname,
          {
            pathParts: {
              id: `${entity?.id}`,
              versionId: `${entity?.versionId}`,
            },
          },
        ),
      data: [
        dashboardHomeItem,
        formTemplatesItem,
        {
          label: t('view-details'),
          path: APP_URLS.dashboard.formTemplates.viewVersion.getDynamicPath({
            pathParts: {
              id: `${entity?.id}`,
              versionId: `${entity?.versionId}`,
            },
          }),
        },
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.formTemplates.duplicate.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.id}`,
            versionId: `${entity?.versionId}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        formTemplatesItem,
        {
          label: t('duplicate-form-template'),
          path: APP_URLS.dashboard.formTemplates.viewVersion.getDynamicPath({
            pathParts: {
              id: `${entity?.id}`,
              versionId: `${entity?.versionId}`,
            },
          }),
        },
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.assets.index.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('assets'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.assets.categories.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        assetsItem,
        {
          label: t('categories'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.assets.add.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        assetsItem,
        {
          label: t('add-new-asset'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.assets.edit.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        assetsItem,
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.assets.view.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        assetsItem,
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.assets.nomenclature.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.id}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        assetsItem,
        {
          label: t('view-asset'),
          path: APP_URLS.dashboard.assets.view.getDynamicPath({
            pathParts: {
              id: `${entity?.id}`,
            },
          }),
        },
        {
          label: t('nomenclature'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.assets.counter.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        assetsItem,
        {
          label: t('view-asset'),
          path: APP_URLS.dashboard.assets.view.getDynamicPath({
            pathParts: {
              id: `${entity?.id}`,
            },
          }),
        },
        {
          label: t('counters'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.assets.counters.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        assetsItem,
        {
          label: t('counter-monitoring'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.assets.documentation.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.id}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        assetsItem,
        {
          label: t('view-asset'),
          path: APP_URLS.dashboard.assets.view.getDynamicPath({
            pathParts: {
              id: `${entity?.id}`,
            },
          }),
        },
        {
          label: t('documentation'),
        },
      ],
    },
    {
      condition:
        APP_URLS.dashboard.interventions.index.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('interventions'),
        },
      ],
    },
    {
      condition:
        APP_URLS.dashboard.interventions.categories.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        interventionsItem,
        {
          label: t('categories'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.interventions.add.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        interventionsItem,
        {
          label: t('create-intervention'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.interventions.edit.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.id}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        interventionsItem,
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.interventions.view.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.id}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        interventionsItem,
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.interventions.viewActions.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.id}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        interventionsItem,
        {
          label: t('view-intervention'),
          path: APP_URLS.dashboard.interventions.view.getDynamicPath({
            pathParts: {
              id: `${entity?.id}`,
            },
          }),
        },
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.interventions.actionAdd.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.id}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        interventionsItem,
        {
          label: t('view-intervention'),
          path: APP_URLS.dashboard.interventions.viewActions.getDynamicPath({
            pathParts: {
              id: `${entity?.id}`,
            },
          }),
        },
        {
          label: t('create-an-action'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.interventions.actionEdit.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.interventionId}`,
            actionId: `${entity?.id}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        interventionsItem,
        {
          label: t('view-intervention'),
          path: APP_URLS.dashboard.interventions.viewActions.getDynamicPath({
            pathParts: {
              id: `${entity?.interventionId}`,
            },
          }),
        },
        {
          label: t('edit-an-action'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.interventions.actionView.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.interventionId}`,
            actionId: `${entity?.id}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        interventionsItem,
        {
          label: t('view-intervention'),
          path: APP_URLS.dashboard.interventions.viewActions.getDynamicPath({
            pathParts: {
              id: `${entity?.interventionId}`,
            },
          }),
        },
        {
          label: t('view-an-action'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.assets.interventions.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.id}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        assetsItem,
        {
          label: t('view-asset'),
          path: APP_URLS.dashboard.assets.view.getDynamicPath({
            pathParts: {
              id: `${entity?.id}`,
            },
          }),
        },
        {
          label: t('interventions'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.assets.document.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.assetId}`,
          documentId: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        assetsItem,
        {
          label: t('view-asset'),
          path: APP_URLS.dashboard.assets.view.getDynamicPath({
            pathParts: {
              id: `${entity?.assetId}`,
            },
          }),
        },
        {
          label: t('documentation'),
          path: APP_URLS.dashboard.assets.documentation.getDynamicPath({
            pathParts: {
              id: `${entity?.assetId}`,
            },
            params: {
              ...DEFAULT_LIST_PARAMS,
            },
          }),
        },
        {
          label: t('edit-document'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.assets.document.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.assetId}`,
          documentId: NEW,
        },
      }),
      data: [
        dashboardHomeItem,
        assetsItem,
        {
          label: t('view-asset'),
          path: APP_URLS.dashboard.assets.view.getDynamicPath({
            pathParts: {
              id: `${entity?.assetId}`,
            },
          }),
        },
        {
          label: t('documentation'),
          path: APP_URLS.dashboard.assets.documentation.getDynamicPath({
            pathParts: {
              id: `${entity?.assetId}`,
            },
            params: {
              ...DEFAULT_LIST_PARAMS,
            },
          }),
        },
        {
          label: t('upload-document'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.tasks.index.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('tasks'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.tasks.add.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        tasksItem,
        {
          label: t('create-task'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.tasks.edit.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        tasksItem,
        {
          label: t('task-details'),
          path: APP_URLS.dashboard.tasks.view.getDynamicPath({
            pathParts: {
              id: `${entity?.id}`,
            },
          }),
        },
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.tasks.duplicate.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        tasksItem,
        {
          label: t('duplicate-task'),
          path: APP_URLS.dashboard.tasks.view.getDynamicPath({
            pathParts: {
              id: `${entity?.id}`,
            },
          }),
        },
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.tasks.view.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        tasksItem,
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.tasks.formView.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        tasksItem,
        {
          label: t('task-details'),
          path: APP_URLS.dashboard.tasks.view.getDynamicPath({
            pathParts: {
              id: `${entity?.id}`,
            },
          }),
        },
        {
          label: entity?.name,
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.tasks.reporting.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [
        dashboardHomeItem,
        tasksItem,
        {
          label: t('task-details'),
          path: APP_URLS.dashboard.tasks.view.getDynamicPath({
            pathParts: {
              id: `${entity?.id}`,
            },
          }),
        },
        {
          label: t('reporting'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.tasks.interventions.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.id}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        tasksItem,
        {
          label: t('task-details'),
          path: APP_URLS.dashboard.tasks.view.getDynamicPath({
            pathParts: {
              id: `${entity?.id}`,
            },
          }),
        },
        {
          label: t('interventions'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.tasks.approvals.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('approvals'),
        },
      ],
    },
    {
      condition:
        APP_URLS.dashboard.companyDomains.index.isTheSameUrlAs(pathname),
      data: [dashboardHomeItem, { label: t('domains') }],
    },
    {
      condition: APP_URLS.dashboard.sso.index.isTheSameUrlAs(pathname),
      data: [dashboardHomeItem, { label: t('single-sign-on-sso') }],
    },
    {
      condition: APP_URLS.dashboard.sso.add.isTheSameUrlAs(pathname),
      data: [dashboardHomeItem, ssoItem, { label: t('add-sso-connection') }],
    },
    {
      condition: APP_URLS.dashboard.sso.edit.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [dashboardHomeItem, ssoItem, { label: t('edit-sso-connection') }],
    },
    {
      condition: APP_URLS.dashboard.sso.view.isTheSameUrlAs(pathname, {
        pathParts: {
          id: `${entity?.id}`,
        },
      }),
      data: [dashboardHomeItem, ssoItem, { label: t('view-sso-connection') }],
    },
    {
      condition: APP_URLS.dashboard.dataImport.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('import'),
        },
      ],
    },
    {
      condition:
        APP_URLS.dashboard.formChecklists.index.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        {
          label: t('forms-checklists-templates'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.formChecklists.add.isTheSameUrlAs(pathname),
      data: [
        dashboardHomeItem,
        checklistsItem,
        {
          label: t('create-new-checklist'),
        },
      ],
    },
    {
      condition: APP_URLS.dashboard.formChecklists.edit.isTheSameUrlAs(
        pathname,
        {
          pathParts: {
            id: `${entity?.id}`,
          },
        },
      ),
      data: [
        dashboardHomeItem,
        checklistsItem,
        {
          label: t('edit-checklist'),
        },
      ],
    },
  ];

  const matchedCrumb = breadcrumbMap.find((crumb) => crumb.condition);
  return (matchedCrumb ? matchedCrumb.data : []) as BreadCrumb[];
}
